import { RECENT_ACTIVITIES_COUNT } from "shared/constants/appConstants";
import { ID } from "shared/interfaces/common";

export default {
    ADMINISTRATION: {
        USERS: {
            ROOT: "api/v1/admin/users",
            USER_PROFILE_BY_USERID: (userId: string | number): string => `api/v1/admin/users/${userId}`,
            CHANGE_PROFILE_EMAIL: (userId: string | number): string =>
                `api/v1/admin/users/${userId}/change-email-request`,
            CHANGE_PROFILE_PHONE_NUMBER: (userId: string | number): string =>
                `api/v1/admin/users/${userId}/change-phone-request`,
            ACCOUNT_TYPES: "api/v1/lookups/account-types?excludeAdmin=true",
            DEACTIVATE_USER_BY_ID: (userId: string | number): string => `api/v1/admin/users/${userId}/deactivate`,
            INVITE_LANDLORD: "api/v1/admin/users/invite-landlord",
            ACTIVATE_USER_BY_ID: (userId: string | number): string => `api/v1/admin/users/${userId}/activate`,
            DELETE_USER: (userId: ID): string => `api/v1/admin/users/${userId}/delete`,
            CREATE: {
                TENANT: "api/v1/admin/users/tenant/new",
                CORPORATE_LL: "api/v1/admin/users/corporate-landlord/new",
                PRIVATE_LL: "api/v1/admin/users/private-landlord/new",
                AGENT: "api/v1/admin/users/agent/new",
            },
        },
        FAILED_PAYMENTS: {
            ROOT: "api/v1/admin/failed-payments",
        },
        VOUCHERS: {
            ROOT: "api/v1/admin/vouchers",
            USER_VOUCHERS: (userId: string | number): string => `api/v1/admin/vouchers/${userId}`,
            REQUESTED: (voucherId: string | number): string => `api/v1/admin/vouchers/${voucherId}/requested`,
            DELETE_EXPIRED: `api/v1/admin/vouchers/expired`,
            DELETE_COMPLETED: `api/v1/admin/vouchers/completed`,
            VALID: "api/v1/admin/vouchers/valid",
        },
        VOUCHER_SETTINGS: {
            ROOT: (userId: string | number): string => `api/v1/admin/voucher-settings/${userId}`,
            TOGGLE: (userId: string | number): string => `api/v1/admin/voucher-settings/${userId}/toggle`,
            PAID_VOUCHERS: (userId: number | string): string => `api/v1/admin/voucher-settings/${userId}/paid-vouchers`,
        },
        VOUCHER_METRICS: {
            ROOT: "api/v1/admin/voucher-metrics",
            TOTALS: "api/v1/admin/voucher-metrics/totals",
            USER: (userId: ID): string => `api/v1/admin/voucher-metrics/${userId}`,
        },
        VOUCHER_INVOICES: {
            ROOT: "api/v1/admin/voucher-invoices",
        },
        COMPANIES: {
            ROOT: "api/v1/admin/companies",
            BY_ID: (id: number): string => `api/v1/admin/companies/${id}`,
            BRANCHES: (id: number): string => `api/v1/admin/companies/branches/${id}`,
            UPDATE_COMPANY_PROFILE_BY_ID: (companyId: number | string): string => `api/v1/admin/companies/${companyId}`,
            UPDATE_COMPANY_DETAILS_BY_ID: (companyId: number | string): string =>
                `api/v1/admin/companies/${companyId}/details`,
            UPDATE_COMPANY_ADDRESS_BY_ID: (companyId: number | string): string =>
                `api/v1/admin/companies/${companyId}/primary-address`,
            UPDATE_COMPANY_CONTACTS_BY_ID: (companyId: number | string): string =>
                `api/v1/admin/companies/${companyId}/contacts`,
            UPDATE_COMPANY_HONESTY_QUESTIONS_BY_ID: (companyId: number | string): string =>
                `api/v1/admin/companies/${companyId}/honesty-questions`,
            UPDATE_COMPANY_PRIMARY_CONTACT: `api/v1/admin/companies/primary-contact`,
            COMPANY_USERS_BY_ID: (companyId: ID): string => `api/v1/admin/companies/${companyId}/users`,
            RESEND_INVITATION: (companyId: ID): string => `api/v1/admin/companies/${companyId}/invitation/resend`,
            DELETE_INVITE: (companyId: ID, userId: ID): string =>
                `api/v1/admin/companies/${companyId}/invitation/${userId}`,
        },
        MEMBERSHIP_RATES: {
            ROOT: "api/v1/admin/membership-rates",
            SERVICE_FEE: "api/v1/admin/membership-rates/service-fee",
        },
        PROPERTIES: {
            ROOT: "api/v1/admin/properties",
            COMPANY: (userId: number): string => `api/v1/admin/companies/properties/${userId}`,
            USER: (userId: number): string => `api/v1/admin/users/${userId}/properties`,
            PROPERTY: (propertyId: ID): string => `api/v1/admin/properties/${propertyId}`,
            MOVE_PROPERTY_TO_LANDLORD: (propertyId: ID): string =>
                `api/v1/admin/properties/${propertyId}/move-to-landlord`,
            MOVE_PROPERTY_TO_AGENCY: (propertyId: ID, agencyId: ID): string =>
                `api/v1/admin/properties/${propertyId}/move-to-agency?agencyId=${agencyId}`,
            FETCH_PROPERTY_MEMBERSHIP: (propertyId: ID): string => `api/v1/admin/properties/${propertyId}/membership`,
            FINISH_MEMBERSHIP_INTERNAL: (propertyId: ID): string => `api/v1/admin/properties/${propertyId}/membership`,
            MEMBERSHIP_PDF: (propertyId: ID): string => `api/v1/admin/properties/${propertyId}/membership/pdf`,
        },
        ROOT: "api/v1/admin",
        REMOVE_INVITATION: "api/v1/admin/remove-invitation",
        INVITATIONS: "api/v1/admin/invitations",
        RESEND_INVITATION: "api/v1/admin/invitations/resend",
        DELETE_INVITATION_BY_USERID: (userId: number): string => `api/v1/admin/invitations/${userId}`,
        INTERNAL_USER_ROLES: "api/v1/admin/roles",
        INTERNAL_USER_PERMISSIONS: "api/v1/admin/roles/permissions",
        INTERNAL_USER_ROLE_BY_ID: (roleId: number): string => `api/v1/admin/roles/${roleId}`,
        COMPANY_ROLES_FOR_INTERNAL_USER: (companyId: number | string): string =>
            `api/v1/admin/companies/${companyId}/roles`,
        ASSIGN_COMPANY_ROLES_BY_INTERNAL_USER: (companyId: number | string, userId: number | string): string =>
            `api/v1/admin/companies/${companyId}/users/${userId}/assign-roles`,
        UPDATE_PP_AND_TC: "api/v1/admin/general-settings",
        RESOLUTION_MANAGEMENT: {
            PROPERTY: (propertyId: number): string => `api/v1/admin/resolutions-management/${propertyId}`,
            CLAIM_HISTORY: (propertyId: number): string =>
                `api/v1/admin/resolutions-management/${propertyId}/claim-history`,
            ACCEPT: (claimId: number): string => `api/v1/admin/resolutions-management/${claimId}/accept`,
            DECLINE: (claimId: number): string => `api/v1/admin/resolutions-management/${claimId}/decline`,
        },
        CLAIMS: "api/v1/admin/claims",
        PAYMENT: {
            PAYMENT_DETAILS_WITH_ID: (tenantId: number | string): string =>
                `api/v1/admin/payments/payment-details?userID=${tenantId}`,
            PAYMENT_DETAILS: "api/v1/admin/payments/payment-details",
        },
        TENANCY: {
            SET_PAYMENT_VARIANT: "api/v1/admin/tenancies/payment-variant",
            RESET_PAYMENT_VARIANT: (tenancyId: ID): string =>
                `api/v1/admin/tenancies/${tenancyId}/payment-variant/reset`,
            CONFIRM_MEMBERSHIP: "api/v1/admin/tenancies/confirm-membership",
            CONFIRM_MEMBERSHIP_WITH_VOUCHER: "api/v1/admin/tenancies/confirm-membership-with-voucher",
            UPDATE_PAYMENT_VARIANT_AND_SUBSCRIPTIONS: "api/v1/admin/tenancies/payment-variant-and-subscriptions",
        },
        END_OF_TENANCY: {
            ROOT: "api/v1/admin/end-of-tenancies",
            TAB_RENDER_INFO: "api/v1/admin/end-of-tenancies/tab-render-info",
            TAB_RENDER_INFO_BY_PROPERTY_ID: (propertyId: ID): string =>
                `api/v1/admin/end-of-tenancies/tab-render-info?propertyId=${propertyId}`,
            CANCEL: "api/v1/admin/end-of-tenancies/cancel",
        },
    },
    COMPANY: {
        USERS: "api/v1/company/users",
        USER: (userId: number): string => `api/v1/company/users/${userId}`,
        INVITATIONS: "api/v1/company/invitations",
        RESEND_INVITATION: "api/v1/company/invitations/resend",
        DELETE_INVITATION_BY_USERID: (userId: number): string => `api/v1/company/invitations/${userId}`,
        COMPANY_ROLES: "api/v1/company/roles",
        COMPANY_PERMISSIONS: "api/v1/company/permissions",
        COMPANY_ROLE_BY_ID: (roleId: ID): string => `api/v1/company/roles/${roleId}`,
        ASSIGN_ROLE_BY_USERID: (userId: ID): string => `api/v1/company/users/${userId}/assign-roles`,
        CHANGE_EMAIL_BY_USERID: (userId: ID): string => `api/v1/company/users/${userId}/change-email-request`,
    },
    AUTH: {
        REQUEST_LOGIN_CODE: "api/v1/auth/request-login-code",
        RESEND_LOGIN_EXPIRED_LINK: "api/v1/auth/resend-login-link-by-auth-token",
    },
    ACCOUNT: {
        REGISTER: "api/v1/account/register",
        RESEND: "api/v1/account/resend-email",
        RESEND_REG_EXPIRED_LINK: "api/v1/account/resend-email-by-auth-token",
        CHANGE_EMAIL_REQUEST: "api/v1/account/change-email-request",
        CONFIRM_CHANGE_EMAIL_REQUEST: "api/v1/account/change-email-request/confirm",
        RESEND_CHANGE_EMAIL_LINK: "api/v1/account/change-email-request/resend",
        RESEND_INVITATION_LINK: "api/v1/account/invitations/resend",
        CHANGE_PHONE_REQUEST: "api/v1/account/change-phone-request",
        AUTHORISE_INVITATION: "api/v1/account/authorise-invitation",
        MISSING_DETAILS: "api/v1/account/missing-details",
    },
    FOOTER_LINKS: "api/v1/settings/privacy-policy-and-terms-and-conditions",
    LOOKUP: {
        ADDRESSES: "api/v1/lookups/addresses",
        HONESTY_QUESTION: "api/v1/lookups/honesty-questions",
        COUNTRIES: "api/v1/lookups/countries",
        USER_STATUSES: "api/v1/lookups/user-statuses",
        TENANCY_STATUSES: "api/v1/lookups/tenancy-statuses",
        EMPLOYMENT_STATUSES: "api/v1/lookups/employment-statuses",
        CLAIM_STATUSES: "api/v1/lookups/claim-statuses",
        PRIMARY_CONTACTS: "api/v1/lookups/alternative-primary-contacts",
        COMPANIES_HOUSES: "api/v1/lookups/companies-houses",
        DEACTIVATION_REASONS: "api/v1/lookups/deactivation-reasons",
        FREQUENCIES_OF_RENT: "api/v1/lookups/rent-frequencies",
        AGENCIES: "api/v1/lookups/agencies",
        COMPANIES: "api/v1/lookups/companies",
        AGENCY_BRANCHES: (id: number): string => `api/v1/lookups/agency-branches/${id}`,
        LANDLORDS: "api/v1/lookups/landlords",
        AGENCY_LANDLORDS: "api/v1/lookups/agency-landlords",
        BRANCHES: "api/v1/lookups/branches",
        LANDLORD_PROPERTIES: (landlordId: number): string =>
            `api/v1/lookups/landlord-properties?landlordId=${landlordId}&loadAllItems=false`,
        TENANCY_TENANTS: (propertyId: ID): string => `api/v1/lookups/tenancy-tenants?propertyId=${propertyId}`,
        TENANCY_TENANTS_THAT_SHOULD_CONFIRM_MEMBERSHIP: (tenancyId: ID): string =>
            `api/v1/lookups/tenancy-tenants-that-should-confirm-membership?loadAllItems=true&tenancyID=${tenancyId}`,
        POSSIBLE_END_OF_TENANCY_INITIATORS: "api/v1/lookups/possible-end-of-tenancy-initiators",
        FREE_TENANTS: "api/v1/lookups/free-tenants",
    },
    AGENT: {
        REGISTER_AGENT: "api/v1/agent",
        INVITE_LANDLORD: "api/v1/agent/invite-landlord",
        ADD_PROPERTY_TO_LANDLORD: "api/v1/agent/add-property-to-landlord",
    },
    AGENCY: {
        VERIFY_REGISTRATION_NUMBER: "api/v1/company/verify-company-registration-number",
        BRANCHES: "api/v1/company/branches",
    },
    LANDLORD: {
        REGISTER_CORPORATE_LL: "api/v1/corporate-landlord",
        REGISTER_PRIVATE_LL: "api/v1/private-landlord",
    },
    TENANT: {
        REGISTER_TENANT: "api/v1/tenant",
        INVITE: "api/v1/tenant/invitation",
        CAN_BE_INVITED_TO_TENANCY: "api/v1/tenant/can-be-invited-to-tenancy",
    },
    MENU: {
        ROOT: "api/v1/menu",
    },
    PROFILE: {
        UPDATE_AGENT: "api/v1/agent/profile",
        UPDATE_CORPORATE_LANDLORD: "api/v1/corporate-landlord/profile",
        UPDATE_PRIVATE_LANDLORD: "api/v1/private-landlord/profile",
        UPDATE_TENANT: "api/v1/tenant/profile",
        UPDATE_ADMIN: "api/v1/admin/profile",
    },
    POLICY: {
        ACCEPT_POLICY: "api/v1/auth/privacy-policy-and-terms-and-conditions/confirmed",
    },
    COMPANY_DETAILS: {
        ROOT: "api/v1/company",
        PRIMARY_CONTACTS: "api/v1/company/primary-contact",
    },
    TENANCY: {
        ROOT: "api/v1/tenancies",
        MEMBERSHIP: "api/v1/tenancies/membership",
        BILLING: "api/v1/tenancies/billing",
        COUNT_TRADITIONAL_DEPOSIT_AMOUNT: "api/v1/tenancies/count-traditional-deposit-amount",
        LEAD_TENANT_AS_LOOKUP: "api/v1/tenancies/lead-tenant",
        SET_PAYMENT_VARIANT: "api/v1/tenancies/payment-variant",
        RESET_PAYMENT_VARIANT: "api/v1/tenancies/payment-variant/reset",
        CONFIRM_MEMBERSHIP: "api/v1/tenancies/confirm-membership",
        CONFIRM_MEMBERSHIP_WITH_VOUCHER: "api/v1/tenancies/confirm-membership-with-voucher",
        REMOVE_TENANT_FROM_TENANCY: (propertyId: ID, tenantId: ID): string =>
            `api/v1/tenancies/${propertyId}/tenant/${tenantId}`,
        MEMBERSHIP_PDF: "api/v1/tenancies/membership/pdf",
        MEMBERSHIP_PDF_BY_ID: (propertyId: ID): string => `api/v1/tenancies/${propertyId}/membership/pdf`,
        RESEND_INVITATION: (propertyId: ID, tenantId: ID): string =>
            `api/v1/tenancies/${propertyId}/tenant/${tenantId}/resend-invitation`,
    },
    PAYMENT: {
        PAYMENT_DETAILS: "api/v1/payments/payment-details",
        SETUP_INTENT: "api/v1/payments/setup-intent",
    },
    VOUCHERS: {
        ROOT: "api/v1/vouchers",
        REQUESTED: (voucherId: number | string): string => `api/v1/vouchers/${voucherId}/requested`,
        EXPIRED: "api/v1/vouchers/expired",
        COMPLETED: "api/v1/vouchers/completed",
        VALID: "api/v1/vouchers/valid",
    },
    PROPERTY: {
        ROOT: "api/v1/properties",
        ROOT_WITH_ID: (propertyId: ID): string => `api/v1/properties/${propertyId}`,
        DETAILS: (propertyId: ID): string => `api/v1/properties/${propertyId}`,
        LANDLORD_VIEW: (propertyId: ID): string => `api/v1/properties/${propertyId}/landlord-view`,
        TENANTS: (propertyId: ID): string => `api/v1/properties/${propertyId}/tenants`,
    },
    END_OF_TENANCY: {
        ROOT: "api/v1/end-of-tenancies",
        UPDATE_AND_CONFIRM_PAYMENT_VARIANT: "api/v1/end-of-tenancies/update-and-confirm-payment-variant",
        RESET_PAYMENT_VARIANT: "api/v1/end-of-tenancies/payment-variant/reset",
        ALTERNATIVE_LEAD_TENANTS: "api/v1/end-of-tenancies/alternative-lead-tenants",
        REQUIRED_CONFIRMATIONS: "api/v1/end-of-tenancies/required-confirmations",
    },
    END_OF_TENANCY_CONFIRMATION: {
        ROOT: (tenancyID: number): string => `api/v1/end-of-tenancy-confirmations/${tenancyID}`,
    },
    BULK_UPLOAD: {
        CSV_PROPERTIES_IMPORT: "api/v1/csv-properties-import",
        CSV_PROPERTIES_IMPORT_VALIDATION: "api/v1/csv-properties-import/valid",
        NOTIFICATIONS_SETTINGS: "api/v1/csv-properties-import/notifications-settings",
        RUNNING_IN_BACKGROUND: "api/v1/csv-properties-import/running-in-background",
        CANCEL_IMPORT_PROPERTIES: "api/v1/csv-properties-import/cancel",
        APPROVE_CREATION_PROPERTIES: "api/v1/csv-properties-import/approve",
        IMPORTED_PROPERTIES: "api/v1/csv-properties-import/imported",
        ERROR_LOG: "api/v1/csv-properties-import/error-log",
    },
    VOUCHER_METRICS: {
        ROOT: "api/v1/voucher-metrics",
    },
    RESOLUTION_MANAGEMENT: {
        PROPERTY_MANAGER_VIEW: "api/v1/resolutions-management/property-manager-view",
        TENANT_VIEW: "api/v1/resolutions-management/tenant-view",
        ACCEPT: (claimId: number): string => `api/v1/resolutions-management/${claimId}/accept`,
        DECLINE: (claimId: number): string => `api/v1/resolutions-management/${claimId}/decline`,
        STATUS: (claimId: number): string => `api/v1/resolutions-management/${claimId}/status`,
        CLOSE: (claimId: number): string => `api/v1/resolutions-management/${claimId}/close`,
    },
    CLAIM: {
        ROOT: "api/v1/claims",
        UPLOAD_EVIDENCE: "api/v1/claims/evidences",
        ATTACH_EVIDENCE: (claimId: number): string => `api/v1/claims/${claimId}/evidences`,
        DOWNLOAD_EVIDENCE: (evidenceId: number): string => `api/v1/claims/evidences/${evidenceId}`,
        DELETE_EVIDENCE: (evidenceId: number): string => `api/v1/claims/evidences/${evidenceId}`,
    },
    DASHBOARD: {
        TOTAL_PROPERTIES_COUNT: "api/v1/dashboards/total-properties-count",
        AGENT_TENANCIES_STATISTICS: "api/v1/dashboards/agent-tenancies-statistics",
        PROPERTIES_BY_STATUS: "api/v1/dashboards/properties-count-by-statuses",
        TENANCIES_ENDING_TODAY: `api/v1/dashboards/tenancies-ending-today?tenanciesNumber=${RECENT_ACTIVITIES_COUNT}`,
        NEW_LIVE_TENANCIES: `api/v1/dashboards/latest-live-tenancies?tenanciesNumber=${RECENT_ACTIVITIES_COUNT}`,
        MONTHLY_RECURRING_REVENUE: "api/v1/dashboards/monthly-recurring-revenue",
        ANNUAL_RECURRING_REVENUE: "api/v1/dashboards/yearly-recurring-revenue",
        RECENT_ACTIVITIES: {
            CLAIMS: "api/v1/dashboards/recent-activities/claims",
            INVITATIONS: "api/v1/dashboards/recent-activities/invitations",
        },
        TOTAL_TRADITIONAL_DEPOSIT_AMOUNT: "api/v1/dashboards/total-traditional-deposit-amount",
    },
};
